import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../../context/ConfigContext';
import Breadcrumbs, { buildSettingsBreadcrumbs } from '../../components/Breadcrumbs';

function CustomDomainSettings() {
  const navigate = useNavigate();
  const { isDemoMode, getTenantSubdomain } = useConfig();
  const demoDisabled = isDemoMode();
  const demoStyle = demoDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {};
  const [customDomain, setCustomDomain] = useState('');
  const [savedDomain, setSavedDomain] = useState(null);
  const [domainStatus, setDomainStatus] = useState('none'); // none, pending, active, error
  const [verifying, setVerifying] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Get tenant's actual subdomain from config context
  const tenantSubdomain = getTenantSubdomain();
  const defaultDomain = tenantSubdomain ? `${tenantSubdomain}.deedstack.com` : 'your-site.deedstack.com';

  useEffect(() => {
    // Load saved domain from localStorage (placeholder for backend)
    const saved = localStorage.getItem('deedstack_custom_domain');
    const status = localStorage.getItem('deedstack_domain_status');
    if (saved) {
      setSavedDomain(saved);
      setCustomDomain(saved);
      setDomainStatus(status || 'pending');
    }
  }, []);

  const handleSaveDomain = () => {
    if (!customDomain.trim()) {
      alert('Please enter a domain');
      return;
    }

    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-_.]+\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(customDomain.trim())) {
      alert('Please enter a valid domain (e.g., loans.mycompany.com)');
      return;
    }

    // Save to localStorage (placeholder for backend)
    localStorage.setItem('deedstack_custom_domain', customDomain.trim());
    localStorage.setItem('deedstack_domain_status', 'pending');
    setSavedDomain(customDomain.trim());
    setDomainStatus('pending');
  };

  const handleVerifyDns = async () => {
    setVerifying(true);

    // Simulate DNS verification (placeholder for backend)
    setTimeout(() => {
      // For demo, randomly succeed or stay pending
      const success = Math.random() > 0.5;
      if (success) {
        setDomainStatus('active');
        localStorage.setItem('deedstack_domain_status', 'active');
        alert('DNS verified successfully! Your custom domain is now active.');
      } else {
        alert('DNS records not found yet. Please ensure you\'ve added the CNAME record and wait for propagation (up to 48 hours).');
      }
      setVerifying(false);
    }, 2000);
  };

  const handleRemoveDomain = () => {
    if (window.confirm('Are you sure you want to remove your custom domain?')) {
      localStorage.removeItem('deedstack_custom_domain');
      localStorage.removeItem('deedstack_domain_status');
      setSavedDomain(null);
      setCustomDomain('');
      setDomainStatus('none');
    }
  };

  const getStatusBadge = () => {
    switch (domainStatus) {
      case 'active':
        return <span className="domain-status-badge domain-status-active">🟢 Active</span>;
      case 'pending':
        return <span className="domain-status-badge domain-status-pending">🟡 Pending DNS</span>;
      case 'error':
        return <span className="domain-status-badge domain-status-error">🔴 Error</span>;
      default:
        return null;
    }
  };

  const faqItems = [
    {
      id: 'propagation',
      question: 'How long does DNS propagation take?',
      answer: 'DNS changes can take anywhere from a few minutes to 48 hours to propagate worldwide. Most changes complete within 1-4 hours. If verification fails, wait a bit longer and try again.'
    },
    {
      id: 'root-domain',
      question: 'Can I use my root domain (e.g., mycompany.com)?',
      answer: 'Yes, but we recommend using a subdomain (e.g., loans.mycompany.com) for best results. Root domains require an ALIAS or ANAME record which not all DNS providers support. Subdomains work with standard CNAME records supported by all providers.'
    },
    {
      id: 'verification-fails',
      question: 'What if DNS verification keeps failing?',
      answer: 'First, double-check that you\'ve added the correct CNAME record with the exact values shown. Ensure there are no typos and the TTL is set correctly. If it still fails after 48 hours, contact support@deedstack.com for assistance.'
    }
  ];

  const providerLinks = [
    { name: 'GoDaddy', url: 'https://www.godaddy.com/help/add-a-cname-record-19236' },
    { name: 'Namecheap', url: 'https://www.namecheap.com/support/knowledgebase/article.aspx/9646/2237/how-to-create-a-cname-record-for-your-domain/' },
    { name: 'Cloudflare', url: 'https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/' },
    { name: 'Squarespace', url: 'https://support.squarespace.com/hc/en-us/articles/360002101888-Adding-custom-DNS-records-to-your-Squarespace-managed-domain' }
  ];

  // Extract subdomain for DNS instructions
  const getSubdomain = () => {
    if (!savedDomain) return 'loans';
    const parts = savedDomain.split('.');
    if (parts.length > 2) {
      return parts[0];
    }
    return '@';
  };

  return (
    <div className="admin-dashboard-container">
      {/* Header with Breadcrumbs */}
      <div className="admin-hub-header">
        <Breadcrumbs items={buildSettingsBreadcrumbs('Custom Domain')} />
        <button
          onClick={() => navigate('/admin/settings-hub')}
          className="admin-hub-back-btn"
        >
          <span className="admin-hub-back-arrow">←</span>
          Back to Settings
        </button>
        <h1 className="admin-page-title">Custom Domain</h1>
        <p className="admin-hub-subtitle">Connect your own domain to your DeedStack platform</p>
      </div>

      {/* Current Status Section */}
      <div className="card custom-domain-status-card">
        <h2>Current Domain</h2>
        <div className="domain-status-display">
          {savedDomain ? (
            <>
              <div className="domain-current">
                <span className="domain-url">{savedDomain}</span>
                {getStatusBadge()}
              </div>
              <button
                className="btn btn-small btn-secondary"
                onClick={() => !demoDisabled && handleRemoveDomain()}
                disabled={demoDisabled}
                style={demoStyle}
                title={demoDisabled ? 'Disabled in demo mode' : ''}
              >
                Remove Domain
              </button>
            </>
          ) : (
            <div className="domain-current">
              <span className="domain-url">{defaultDomain}</span>
              <span className="domain-default-label">(Default)</span>
            </div>
          )}
        </div>
      </div>

      {/* Domain Input Section */}
      <div className="card custom-domain-input-card">
        <h2>{savedDomain ? 'Update Custom Domain' : 'Add Custom Domain'}</h2>
        <p className="domain-input-note">
          Enter your subdomain or domain (e.g., loans.mycompany.com)
        </p>
        <div className="domain-input-group">
          <input
            type="text"
            value={customDomain}
            onChange={(e) => setCustomDomain(e.target.value.toLowerCase())}
            placeholder="loans.mycompany.com"
            className="domain-input"
          />
          <button
            className="btn btn-primary"
            onClick={() => !demoDisabled && handleSaveDomain()}
            disabled={demoDisabled}
            style={demoStyle}
            title={demoDisabled ? 'Disabled in demo mode' : ''}
          >
            Save Domain
          </button>
        </div>
      </div>

      {/* DNS Instructions Section - Show only when domain is saved */}
      {savedDomain && (
        <div className="card custom-domain-dns-card">
          <h2>DNS Configuration</h2>
          <p className="dns-instructions">
            Add the following DNS record to your domain provider:
          </p>

          <div className="dns-table-container">
            <table className="dns-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name / Host</th>
                  <th>Value / Target</th>
                  <th>TTL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>CNAME</code></td>
                  <td><code>{getSubdomain()}</code></td>
                  <td><code>custom.deedstack.com</code></td>
                  <td><code>3600</code></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="dns-verify-section">
            <button
              className="btn btn-primary"
              onClick={() => !demoDisabled && handleVerifyDns()}
              disabled={verifying || domainStatus === 'active' || demoDisabled}
              style={(demoDisabled || verifying || domainStatus === 'active') ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              title={demoDisabled ? 'Disabled in demo mode' : ''}
            >
              {verifying ? 'Verifying...' : domainStatus === 'active' ? 'Verified' : 'Verify DNS'}
            </button>
            {domainStatus === 'active' && (
              <span className="dns-verified-message">Your DNS is configured correctly!</span>
            )}
          </div>

          <div className="dns-provider-links">
            <p>Need help? Here are guides for popular DNS providers:</p>
            <div className="provider-links-grid">
              {providerLinks.map((provider) => (
                <a
                  key={provider.name}
                  href={provider.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="provider-link"
                >
                  {provider.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <div className="card custom-domain-faq-card">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqItems.map((item) => (
            <div
              key={item.id}
              className={`faq-item ${expandedFaq === item.id ? 'faq-expanded' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => setExpandedFaq(expandedFaq === item.id ? null : item.id)}
              >
                <span>{item.question}</span>
                <span className="faq-toggle">{expandedFaq === item.id ? '−' : '+'}</span>
              </button>
              {expandedFaq === item.id && (
                <div className="faq-answer">
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CustomDomainSettings;
